import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/shop';
import { formatMoney } from '../brand/brand';
import { usePageTitle } from '../a11y/usePageTitle';
import type { OrderStatus } from '../api/types';

/**
 * What actually happened.
 *
 * The webhook that issues the voucher is asynchronous, so this page polls — and each
 * poll hits an endpoint that RECONCILES with the payment provider rather than just
 * reading our database. That is what makes the flow survive a webhook that is
 * delayed, blocked by a firewall change, or never delivered at all.
 *
 * Polling stops on a settled state, and stops regardless after a bounded number of
 * attempts. A page that polls for ever is a page someone leaves open on a phone and
 * a request every two seconds until the battery dies.
 */
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;   // 60 seconds

export function OrderResult() {
  const { orderId } = useParams<{ orderId: string }>();

  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [gaveUp, setGaveUp] = useState(false);

  // Tracked separately from `order`, because "the order is still pending" and
  // "every request is failing" are different situations and the page must not
  // present them identically. Without this the give-up branch below is
  // unreachable whenever the endpoint is broken: the `order === null` early
  // return fires first, so a buyer who has PAID sits on "Checking your order…"
  // for ever with no way forward.
  const [lastError, setLastError] = useState<string | null>(null);
  const polls = useRef(0);

  /*
   * THE TITLE IS PART OF THE ANSWER ON THIS PAGE.
   *
   * This screen changes underneath the customer while they wait, and the browser
   * tab is often the only part of it they can see — they switch to their bank app
   * to check the charge and come back. The title says which state it is in.
   */
  usePageTitle(
    order === null
      ? 'Checking your order'
      : order.status === 'paid'
        ? 'Order confirmed'
        : order.status === 'pending'
          ? 'Confirming your payment'
          : 'Payment not completed',
  );

  useEffect(() => {
    if (orderId === undefined) return;

    let cancelled = false;
    let timer: number | undefined;

    async function poll() {
      try {
        const result = await api.order(orderId!);
        if (cancelled) return;

        setOrder(result);
        setLastError(null);

        if (result.status !== 'pending') {
          return;   // settled, one way or the other
        }
      } catch (ex) {
        // Keep polling: a single failure is usually transient, and the attempt cap
        // below stops this being endless. But RECORD it — swallowing the error
        // silently is what let a permanently broken endpoint look identical to a
        // payment that simply had not settled yet.
        if (!cancelled) {
          setLastError(ex instanceof Error ? ex.message : 'Could not reach the server.');
        }
      }

      polls.current += 1;

      if (polls.current >= MAX_POLLS) {
        if (!cancelled) setGaveUp(true);
        return;
      }

      timer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
    }

    void poll();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [orderId]);

  // Never loaded, and we have stopped trying. The buyer has submitted a payment, so
  // the one thing this must not do is imply nothing happened.
  if (order === null && gaveUp) {
    return (
      <div className="panel stack" style={{ gap: 14, maxWidth: 480, margin: '48px auto' }} role="alert">
        <h1 style={{ fontSize: 26 }}>We could not confirm your order</h1>

        <p className="muted">
          Your payment may well have gone through — we simply could not reach our
          system to check. Nothing is lost: if it succeeded, the voucher is in your
          wallet and we have emailed you.
        </p>

        <Link to="/vouchers" className="btn block">Check my vouchers</Link>
        <Link to="/" className="btn secondary block">Back to offers</Link>

        {lastError !== null && (
          <p className="muted" style={{ fontSize: 13 }}>Technical detail: {lastError}</p>
        )}
      </div>
    );
  }

  if (order === null) {
    return (
      <p className="muted" style={{ padding: '48px 0' }} role="status">
        Checking your order…
      </p>
    );
  }

  if (order.status === 'paid') {
    return (
      /* h1, not h2: this is the page's own heading, and the previous h2 left the
         confirmation screen with no h1 at all. */
      <div className="panel stack narrow gap-md" style={{ marginBlock: 40 }} role="status">
        <h1 style={{ fontSize: 26 }}>You&rsquo;re all set</h1>
        <p className="muted">
          We have taken {formatMoney(order.amountCents)} and your voucher is in your
          wallet. We have emailed you a copy too.
        </p>

        {order.voucherId !== null && (
          <Link to={`/vouchers/${order.voucherId}`} className="btn block">
            Show my voucher
          </Link>
        )}

        <Link to="/" className="btn secondary block">Browse more offers</Link>
      </div>
    );
  }

  if (order.status === 'failed' || order.status === 'cancelled') {
    return (
      <div className="panel stack narrow" style={{ gap: 14, marginBlock: 40 }} role="alert">
        <h1 style={{ fontSize: 26 }}>Payment not completed</h1>
        <p className="muted">
          {order.failureMessage ?? 'That payment did not go through. You have not been charged.'}
        </p>
        <Link to="/" className="btn block">Try another offer</Link>
      </div>
    );
  }

  // Still pending. Say what is true rather than spinning silently — and never claim
  // the money did not arrive, because it may well have.
  return (
    /* aria-live on the container rather than the heading: the heading AND the
       paragraph both change when polling gives up, and announcing only half of
       that tells the customer "Still processing" with no explanation. */
    <div className="panel stack" style={{ gap: 14, maxWidth: 480, margin: '48px auto' }} role="status">
      <h1 style={{ fontSize: 26 }}>{gaveUp ? 'Still processing' : 'Finishing up…'}</h1>

      <p className="muted">
        {gaveUp
          ? 'Your payment is taking longer than usual to confirm. If it went through, your voucher will appear in your wallet shortly and we will email you.'
          : 'We are confirming your payment. This usually takes a few seconds.'}
      </p>

      {gaveUp && <Link to="/vouchers" className="btn block">Check my vouchers</Link>}
    </div>
  );
}
